import { AppModule } from "@/store/modules/app";
import { IMultiStartRoute, IResult } from "@/types/map";
// import along from "@turf/along";
// import distance from "@turf/distance";
import * as turf from "@turf/helpers";
import length from "@turf/length";
import lineSlice from "@turf/line-slice";
import Axios from "axios";
import { loadModules } from "esri-loader";

enum Platform {
  NavInfo = "NavInfo",
  AMap = "AMap",
  BMap = "BMap",
  MapABC = "MapABC",
}

interface IDeviceInfo {
  id: string;
  name: string;
  position: number[];
}

interface ICrossInfo {
  id: string;
  name: string;
  lengthFromStart: number;
  position: number[];
  cameras: IDeviceInfo[];
  signals: IDeviceInfo[];
}

export default class DirectionModel_Collinear {
  private static intances: Map<string, DirectionModel_Collinear>;
  public static getInstance(view: __esri.View) {
    const id = view.container.id;
    if (!DirectionModel_Collinear.intances) {
      DirectionModel_Collinear.intances = new Map();
    }
    let instance = DirectionModel_Collinear.intances.get(id);
    if (!instance) {
      instance = new DirectionModel_Collinear(view);
      DirectionModel_Collinear.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.View;
  private routeServiceUrl!: string;
  private reverseGeoCodeServiceUrl!: string;
  private platform = Platform.NavInfo;

  constructor(view: __esri.View) {
    this.view = view;

    if (AppModule.appConfig.routeServiceUrl) {
      this.routeServiceUrl = AppModule.appConfig.routeServiceUrl;
      this.routeServiceUrl = this.routeServiceUrl.replace(
        /{gisserver}/i,
        AppModule.appConfig.gisServer
      );
    }
    if (AppModule.appConfig.reverseGeoCodeServiceUrl) {
      this.reverseGeoCodeServiceUrl =
        AppModule.appConfig.reverseGeoCodeServiceUrl;
      this.reverseGeoCodeServiceUrl = this.reverseGeoCodeServiceUrl.replace(
        /{gisserver}/i,
        AppModule.appConfig.gisServer
      );
    }

    this.platform =
      (AppModule.appConfig.routePlatform as Platform) || Platform.NavInfo;
  }

  /** 路口图层 */
  private crossLayer!: __esri.FeatureLayer;
  /** 信号机图层 */
  private signalLayer!: __esri.FeatureLayer;
  /** 摄像机图层 */
  private cameraLayer!: __esri.FeatureLayer;
  /** 路径图层 */
  private routeLayer!: __esri.FeatureLayer;
  /** 起终点图标等图层 */
  private graphicsLayer!: __esri.GraphicsLayer;

  private Graphic!: __esri.GraphicConstructor;
  private geometryEngineAsync!: __esri.geometryEngineAsync;
  private FeatureLayer!: __esri.FeatureLayerConstructor;
  private webMercatorUtils!: __esri.webMercatorUtils;
  private Polyline!: __esri.PolylineConstructor;

  /** 路径周边缓冲半径 */
  private readonly searchRadius = 50;

  public async initialize(params?: {
    signalUrl?: string;
    cameraUrl?: string;
    crossUrl?: string;
    token?: string;
    showRoad?: boolean;
  }) {
    type MapModules = [
      typeof import("esri/geometry/Polyline"),
      typeof import("esri/Graphic"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/layers/GraphicsLayer"),
      typeof import("esri/geometry/geometryEngineAsync"),
      typeof import("esri/geometry/support/webMercatorUtils")
    ];
    const [
      Polyline,
      Graphic,
      FeatureLayer,
      GraphicsLayer,
      geometryEngineAsync,
      webMercatorUtils,
    ] = await (loadModules([
      "esri/geometry/Polyline",
      "esri/Graphic",
      "esri/layers/FeatureLayer",
      "esri/layers/GraphicsLayer",
      "esri/geometry/geometryEngineAsync",
      "esri/geometry/support/webMercatorUtils",
    ]) as Promise<MapModules>);
    this.Polyline = Polyline;
    this.Graphic = Graphic;
    this.geometryEngineAsync = geometryEngineAsync;
    this.FeatureLayer = FeatureLayer;
    this.webMercatorUtils = webMercatorUtils;

    if (params && params.signalUrl) {
      await this.getAllSignals(params.signalUrl, params.token);
    }

    if (params && params.crossUrl) {
      await this.getAllCrosses(params.crossUrl, params.token);
    }

    if (params && params.cameraUrl) {
      await this.getAllCameras(params.cameraUrl, params.token);
    }

    this.routeLayer = new FeatureLayer({
      source: [],
      objectIdField: "OBJECTID",
      geometryType: "polyline",
      fields: [
        {
          name: "OBJECTID",
          type: "oid",
        },
        {
          name: "status",
          type: "string",
        },
        {
          name: "strategy",
          type: "string",
        },
      ],
      renderer: {
        type: "unique-value",
        field: "status",
        // 非共线部分
        defaultSymbol: {
          type: "simple-line",
          color: "#1345d5",
          width: 4,
        },
        uniqueValueInfos: [
          // 共线部分
          {
            value: "highlight",
            symbol: {
              type: "cim",
              data: {
                type: "CIMSymbolReference",
                symbol: {
                  type: "CIMLineSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidStroke",
                      enable: true,
                      width: 8,
                      color: [0, 189, 44, 255],
                    },
                    {
                      type: "CIMVectorMarker",
                      enable: true,
                      size: 6,
                      markerPlacement: {
                        type: "CIMMarkerPlacementAlongLineSameSize",
                        endings: "WithMarkers",
                        placementTemplate: [20],
                      },
                      frame: {
                        xmin: -6,
                        ymin: -6,
                        xmax: 6,
                        ymax: 6,
                      },
                      markerGraphics: [
                        {
                          type: "CIMMarkerGraphic",
                          geometry: {
                            paths: [
                              [
                                [-8, 5],
                                [0, 0],
                                [-8, -5],
                              ],
                            ],
                          },
                          symbol: {
                            type: "CIMLineSymbol",
                            symbolLayers: [
                              {
                                type: "CIMSolidStroke",
                                enable: true,
                                color: [255, 255, 255, 255],
                                width: 2,
                              },
                            ],
                          },
                        },
                      ],
                    },
                  ],
                },
              },
            },
          },
        ],
      } as any,
    });
    this.view.map.add(this.routeLayer);

    this.graphicsLayer = new GraphicsLayer();
    this.view.map.add(this.graphicsLayer);
  }

  /** 获取所有信号机位置，并保存到FeatureLayer */
  private async getAllSignals(signalServiceUrl: string, token?: string) {
    const responseData = signalServiceUrl.endsWith("json")
      ? await Axios.get(signalServiceUrl)
      : await Axios.post(
          signalServiceUrl,
          {
            pageIndex: 1,
            pageSize: 99999999,
          },
          { headers: { token } }
        );
    if (responseData.data && responseData.data.success) {
      const signalData: {
        signalNo: string;
        crossName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const signalGraphics: __esri.Graphic[] = [];
      signalData.forEach((data) => {
        const x = data.longitude,
          y = data.latitude;
        if (x && y) {
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: data.signalNo,
              name: data.crossName,
            },
          });
          signalGraphics.push(graphic);
        }
      });

      this.signalLayer = new this.FeatureLayer({
        source: signalGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.signalLayer);
    }
  }

  /** 获取所有路口位置，并保存到FeatureLayer */
  private async getAllCrosses(crossServiceUrl: string, token?: string) {
    const responseData = crossServiceUrl.endsWith("json")
      ? await Axios.get(crossServiceUrl)
      : await Axios.post(
          crossServiceUrl,
          {
            pageIndex: 1,
            pageSize: 99999999,
          },
          { headers: { token } }
        );
    if (responseData.data && responseData.data.success) {
      const crossData: {
        crossNo: string;
        crossName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const existCrossId: string[] = [];
      const crossGraphics: __esri.Graphic[] = [];
      crossData.forEach((data) => {
        const crossId = data.crossNo,
          x = data.longitude,
          y = data.latitude;
        // 过滤重复数据
        if (!existCrossId.includes(crossId) && !isNaN(x) && !isNaN(y)) {
          existCrossId.push(crossId);
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: crossId,
              name: data.crossName,
            },
          });
          crossGraphics.push(graphic);
        }
      });

      this.crossLayer = new this.FeatureLayer({
        source: crossGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.crossLayer);
    }
  }

  /**
   * 获取所有摄像机位置，并保存到FeatureLayer
   * @param cameraServiceUrl 摄像机服务地址
   */
  private async getAllCameras(cameraServiceUrl: string, token?: string) {
    if (this.platform === Platform.NavInfo) {
      const responseData = await Axios.post(
        cameraServiceUrl,
        {
          pageIndex: 1,
          pageSize: 99999999,
        },
        { headers: { token } }
      );
      if (responseData.data && responseData.data.success) {
        const cameraData: Array<{
          fstrCameraId: string;
          longitude: number;
          latitude: number;
          camName: string;
        }> = responseData.data.data.data;
        const cameraGraphics = cameraData.map((cameraInfo) => {
          return new this.Graphic({
            geometry: {
              type: "point",
              x: cameraInfo.longitude,
              y: cameraInfo.latitude,
            } as any,
            attributes: {
              id: cameraInfo.fstrCameraId,
              name: cameraInfo.camName,
            },
          });
        });
        this.cameraLayer = new this.FeatureLayer({
          source: cameraGraphics,
          objectIdField: "OBJECTID",
          geometryType: "point",
          fields: [
            {
              name: "OBJECTID",
              type: "oid",
            },
            {
              name: "id",
              type: "string",
            },
            {
              name: "name",
              type: "string",
            },
          ],
        });
      }
    } else if (this.platform === Platform.MapABC) {
      const responseData = cameraServiceUrl.endsWith("json")
        ? await Axios.get(cameraServiceUrl)
        : await Axios.post(
            cameraServiceUrl,
            {
              pageIndex: 1,
              pageSize: 99999999,
            },
            { headers: { token } }
          );
      const cameraData: {
        keyboardCode: string;
        devName: string;
        longitude: number;
        latitude: number;
      }[] = responseData.data.data.data;

      const cameraGraphics: __esri.Graphic[] = [];
      cameraData.forEach((data) => {
        const x = data.longitude,
          y = data.latitude;
        if (x && y) {
          const graphic = new this.Graphic({
            geometry: {
              type: "point",
              x,
              y,
            } as any,
            attributes: {
              id: data.keyboardCode,
              name: data.devName,
            },
          });
          cameraGraphics.push(graphic);
        }
      });

      this.cameraLayer = new this.FeatureLayer({
        source: cameraGraphics,
        objectIdField: "OBJECTID",
        geometryType: "point",
        visible: false,
        fields: [
          {
            name: "OBJECTID",
            type: "oid",
          },
          {
            name: "id",
            type: "string",
          },
          {
            name: "name",
            type: "string",
          },
        ],
      });
      this.view.map.add(this.cameraLayer);
    }
  }

  public async getMultiRoutes(params: IMultiStartRoute): Promise<IResult> {
    const { startPoints, endPoint, wayPoints } = params;
    const pointGraphics: __esri.Graphic[] = [];

    const endPointValue = endPoint[0] + "," + endPoint[1];
    pointGraphics.push(
      new this.Graphic({
        geometry: { type: "point", x: endPoint[0], y: endPoint[1] } as any,
        symbol: {
          type: "picture-marker",
          url: "assets/mapIcons/end.png",
          width: "48px",
          height: "55px",
          yoffset: "20px",
        } as any,
      })
    );
    let wayPointsValue = "";
    if (wayPoints) {
      wayPoints.forEach((wayPoint) => {
        wayPointsValue += wayPoint[0] + "," + wayPoint[1] + ";";
      });
      wayPointsValue = wayPointsValue.substr(0, wayPointsValue.length - 1);
    }

    const routes: { crossInfos: ICrossInfo[]; path: number[][] }[] = [];
    await Promise.all(
      startPoints.map(async (startPoint) => {
        const startPointValue = startPoint[0] + "," + startPoint[1];
        pointGraphics.push(
          new this.Graphic({
            geometry: {
              type: "point",
              x: startPoint[0],
              y: startPoint[1],
            } as any,
            symbol: {
              type: "picture-marker",
              url: "assets/mapIcons/begin.png",
              width: "36px",
              height: "40px",
              yoffset: "15px",
            } as any,
          })
        );

        const path = await this.makeRoute({
          startPointValue,
          endPointValue,
          wayPointsValue,
        });

        if (path) {
          const crossInRoute = await this.findCrossInRoute(path);
          routes.push({ path, crossInfos: crossInRoute });
        }
      })
    );

    // 只有一个起点时，画出路线，不做共线分析
    if (routes.length === 1) {
      this.graphicsLayer.addMany(pointGraphics);
      const result = routes[0];
      const routeGraphic = new this.Graphic({
        geometry: { type: "polyline", paths: [result.path] } as any,
        attributes: { status: "highlight" },
      });
      await this.routeLayer.applyEdits({ addFeatures: [routeGraphic] });

      return { status: 0, message: "", result: result.crossInfos };
    } else if (routes.length > 1) {
      this.graphicsLayer.addMany(pointGraphics);

      const collinearIndex = this.findCollinearInRoute(
        routes.map((route) => route.crossInfos)
      );
      const result = routes[0].crossInfos.slice(0, collinearIndex + 1);

      const collinearPoint = routes[0].crossInfos[collinearIndex].position;
      // 用共线起点替换途经点，画出路径
      await this.drawCollinearLine({ ...params, wayPoints: [collinearPoint] });

      // this.showResult(result);
      return { status: 0, message: "", result };
    } else {
      return { status: -1, message: "请提供起点" };
    }
  }

  private showResult(result: ICrossInfo[]) {
    result.forEach((crossInfo) => {
      const crossPosition = crossInfo.position;
      const crossGraphic = new this.Graphic({
        geometry: {
          type: "point",
          x: crossPosition[0],
          y: crossPosition[1],
        } as any,
        symbol: {
          type: "simple-marker",
          color: "red",
        } as any,
      });
      this.graphicsLayer.add(crossGraphic);

      if (crossInfo.cameras.length > 0) {
        const cameraGraphic = new this.Graphic({
          geometry: {
            type: "point",
            x: crossInfo.cameras[0].position[0],
            y: crossInfo.cameras[0].position[1],
          } as any,
          symbol: {
            type: "simple-marker",
            color: "blue",
            size: 4,
          } as any,
        });
        this.graphicsLayer.add(cameraGraphic);
      }

      if (crossInfo.signals.length > 0) {
        const signalGraphic = new this.Graphic({
          geometry: {
            type: "point",
            x: crossInfo.signals[0].position[0],
            y: crossInfo.signals[0].position[1],
          } as any,
          symbol: {
            type: "simple-marker",
            color: "blue",
            size: 4,
          } as any,
        });
        this.graphicsLayer.add(signalGraphic);
      }
    });
  }

  /**
   * 调用路径服务，生成最短路径
   * @param params
   * @returns
   */
  private async makeRoute(params: {
    startPointValue: string;
    endPointValue: string;
    wayPointsValue?: string;
    strategy?: string;
  }): Promise<number[][] | undefined> {
    const { startPointValue, endPointValue, wayPointsValue } = params;
    const requestParams =
      wayPointsValue && wayPointsValue !== ""
        ? {
            origin: startPointValue,
            destination: endPointValue,
            waypoints: wayPointsValue,
          }
        : {
            origin: startPointValue,
            destination: endPointValue,
          };
    const responseData = (
      await Axios.get(this.routeServiceUrl, {
        params: requestParams,
      })
    ).data;
    switch (this.platform) {
      case Platform.NavInfo:
        break;

      case Platform.MapABC:
        return await this.dealResponse_MapABC(responseData);
    }
  }

  /** 处理高德内网的返回数据 */
  private async dealResponse_MapABC(
    responseData: any
  ): Promise<number[][] | undefined> {
    if (responseData.message === "ok") {
      const { steps, duration, distance } = responseData.result.routes[0];
      // 将多个step合并
      let routeString = "";
      steps.forEach((step: any) => {
        routeString += step.path + ";";
      });
      // 去掉最后的分号，否则split后会多一个元素
      routeString = routeString.substr(0, routeString.length - 1);
      // string转成number[][]
      const pointsArray = routeString.split(";");
      const path: number[][] = [];
      pointsArray.forEach((pointString) => {
        const coord = pointString.split(",");
        path.push([Number(coord[0]), Number(coord[1])]);
      });
      return path;
    }
  }

  /**
   * 搜索路径经过的路口，以及路口周边的摄像机、信号机
   * 先做缓冲搜索，然后获取缓冲区内的路口在路径上的投影点
   * 用起点到投影点的距离给路口排序
   * @param path 路径点集
   * @returns
   */
  private async findCrossInRoute(path: number[][]): Promise<ICrossInfo[]> {
    const crossInfos: ICrossInfo[] = [];

    const turfLine = turf.lineString(path);

    const line = new this.Polyline({ paths: [path] });
    const routeBuffer = (await this.geometryEngineAsync.geodesicBuffer(
      line,
      this.searchRadius,
      "meters"
    )) as __esri.Polygon;

    const crossQuery = this.crossLayer.createQuery();
    crossQuery.geometry = routeBuffer;
    const crossGraphics = (await this.crossLayer.queryFeatures(crossQuery))
      .features;

    let crossIds = "";
    for (let crossGraphic of crossGraphics) {
      const crossId =
        crossGraphic.attributes["NodeID"] || crossGraphic.attributes["id"];
      if (crossIds.includes(crossId)) {
        continue;
      }
      crossIds += crossId + ";";

      const crossName = crossGraphic.attributes["Name_1"]
        ? `${crossGraphic.attributes["Name_1"]}-${crossGraphic.attributes["Name_2"]}`
        : crossGraphic.attributes["name"];

      // 路口点在路径上的投影
      const nearestPoint = (
        await this.geometryEngineAsync.nearestCoordinate(
          line,
          crossGraphic.geometry as __esri.Point
        )
      ).coordinate;

      // 起点到投影点的线段
      const slicedLine = lineSlice(
        turf.point(path[0]),
        turf.point([nearestPoint.x, nearestPoint.y]),
        turfLine
      );
      // 起点到投影点的距离
      const lengthFromStart = length(slicedLine, { units: "meters" });

      const crossPoint = crossGraphic.geometry as __esri.Point;
      const device = await this.findDeviceNearCross(crossPoint);

      crossInfos.push({
        id: crossId,
        name: crossName,
        lengthFromStart,
        position: [crossPoint.x, crossPoint.y],
        ...device,
      });
    }

    // 按路口距离排序
    // 倒序，便于分析共线部分
    const sortedCrosses = crossInfos.sort(
      (cross1, cross2) => cross2.lengthFromStart - cross1.lengthFromStart
    );

    return sortedCrosses;
  }

  /**
   * 寻找点位周边的摄像机和信号机
   * @param point
   * @returns
   */
  private async findDeviceNearCross(
    point: __esri.Point
  ): Promise<{ cameras: IDeviceInfo[]; signals: IDeviceInfo[] }> {
    const cameras: IDeviceInfo[] = [];
    const signals: IDeviceInfo[] = [];

    const pointBuffer = (await this.geometryEngineAsync.geodesicBuffer(
      point,
      this.searchRadius,
      "meters"
    )) as __esri.Polygon;

    if (this.signalLayer) {
      const query = this.signalLayer.createQuery();
      query.geometry = pointBuffer;
      query.outFields = ["*"];
      const signalGraphics = (await this.signalLayer.queryFeatures(query))
        .features;
      signalGraphics.forEach((graphic) => {
        const point = graphic.geometry as __esri.Point;
        signals.push({
          id: graphic.attributes["id"],
          name: graphic.attributes["name"],
          position: [point.x, point.y],
        });
      });
    }

    if (this.cameraLayer) {
      const query = this.cameraLayer.createQuery();
      query.geometry = pointBuffer;
      query.outFields = ["*"];
      const cameraGraphics = (await this.cameraLayer.queryFeatures(query))
        .features;
      cameraGraphics.forEach((graphic) => {
        const point = graphic.geometry as __esri.Point;
        cameras.push({
          id: graphic.attributes["id"],
          name: graphic.attributes["name"],
          position: [point.x, point.y],
        });
      });
    }

    return { cameras, signals };
  }

  /**
   * 通过分析路口来找到共线部分
   * @param routes
   * @returns 开始共线的路口序号
   */
  private findCollinearInRoute(routes: ICrossInfo[][]): number {
    let found = false;

    let minLength = Number.MAX_VALUE;
    routes.forEach((route) => (minLength = Math.min(minLength, route.length)));

    let index = 0;
    for (let i = 0; i < minLength; i++) {
      for (let j = 0; j < routes.length - 1; j++) {
        index = i;
        const crossId1 = routes[j][i].id;
        const crossId2 = routes[j + 1][i].id;
        if (crossId1 !== crossId2) {
          index--;
          found = true;
          break;
        }
      }
      if (found) {
        break;
      }
    }
    return index;
  }

  /**
   * 画出共线和非共线
   * @param params
   */
  private async drawCollinearLine(params: IMultiStartRoute) {
    // 非共线部分
    const { startPoints, endPoint, wayPoints } = params;
    const endPointValue = endPoint[0] + "," + endPoint[1];

    // 显示共线点
    if (wayPoints) {
      const wayPoint = wayPoints[0];
      const wayPointValue = wayPoint[0] + "," + wayPoint[1];
      const waypointGraphic = new this.Graphic({
        geometry: { type: "point", x: wayPoint[0], y: wayPoint[1] } as any,
        symbol: {
          type: "picture-marker",
          url: "assets/mapIcons/icon_way.png",
          width: "19px",
          height: "31px",
          yoffset: "15px",
        } as any,
      });
      this.graphicsLayer.add(waypointGraphic);

      const routeGraphics: __esri.Graphic[] = [];

      await Promise.all(
        startPoints.map(async (startPoint) => {
          const startPointValue = startPoint[0] + "," + startPoint[1];
          const path = await this.makeRoute({
            startPointValue,
            endPointValue: wayPointValue,
          });

          const graphic = new this.Graphic({
            geometry: { type: "polyline", paths: [path] } as any,
            attributes: { status: "normal" },
          });
          routeGraphics.push(graphic);
        })
      );

      const path = await this.makeRoute({
        startPointValue: wayPointValue,
        endPointValue,
      });
      const routeGraphic = new this.Graphic({
        geometry: { type: "polyline", paths: [path] } as any,
        attributes: { status: "highlight" },
      });
      routeGraphics.push(routeGraphic);

      await this.routeLayer.applyEdits({ addFeatures: routeGraphics });
      if (this.view.type === "2d") {
        (this.view as __esri.MapView).goTo(routeGraphics);
      } else {
        (this.view as __esri.SceneView).goTo(routeGraphics);
      }
    }
  }

  public async clearRoutes() {
    if (this.routeLayer) {
      const currentGraphics = (
        await this.routeLayer.queryFeatures(this.routeLayer.createQuery())
      ).features;
      await this.routeLayer.applyEdits({ deleteFeatures: currentGraphics });
    }

    if (this.graphicsLayer) {
      this.graphicsLayer.removeAll();
    }
  }
}
